/*
 * @Author: Wang Wenmao
 * @Date: 2023-08-18 15:17:46
 * @LastEditors: Wang Wenmao
 * @LastEditTime: 2024-01-05 15:18:00
 * @Description: 热区编辑器
 */
import React, { Component, createRef } from 'react';
import { Button, Modal, Form } from 'antd';
import HotZoneWrap from 'hot-zone';
import { HotZoneItem } from './hot-zone-item';
import { HotZoneTips } from './hot-zone-tips';
import { EntryList } from '../../design_page/components/tools';
import { customSelectorConfig } from '../../design';
import { mitt } from '../../services';

const FormItem = Form.Item;

// 从url中获取图片宽高
const getUrlImgSize = (path: string) => {
    if (!path) return { imgWidth: 0, imgHeight: 0 };
    const wh = path.split('?')[1];
    const imgWidth = +wh.split('&')[0].split('=')[1];
    const imgHeight = +wh.split('&')[1].split('=')[1];
    return { width: imgWidth, height: imgHeight };
};

// 判断图片方向
const determineImageOrientation = (width: number, height: number) => {
    if (width > height) {
        return 'landscape';
    } else if (height > width) {
        return 'portrait';
    } else {
        return 'square';
    }
};

// 计算图片宽高
const getImgSize = (width: number, height: number) => {
    const ratio = width / height;
    const orientation = determineImageOrientation(width, height);

    if (orientation === 'landscape') {
        const newWidth = 600;
        const newHeight = newWidth / ratio;
        return { width: newWidth, height: newHeight };
    } else if (orientation === 'portrait') {
        const newHeight = 600;
        const newWidth = newHeight * ratio;
        return { width: newWidth, height: newHeight };
    } else {
        const newWidth = 600;
        const newHeight = 600;
        return { width: newWidth, height: newHeight };
    }
};

interface IProps {
    index?: number; // 索引
    wrapName: string; // 表单名称
    visible: boolean; // 是否显示
    imgUrl: string; // 图片地址
    maxCount: number; // 创建最大数量
    form: any; // 表单内容
    linkTypes?: any; // 链接类型
    linkTypeMode: any; // 链接类型模式
    hotZoneList: any[]; // 热区列表
    result: string; // 结果字段
    onChange: (value: any) => void; // 热区列表改变
    onClose: (index?: number) => void; // 关闭
    readOnly?: boolean; // 是否只读;
    selector?: any;
    isFormTrigger?: any; // 是不是通过form触发
    source?: { from: string; data: any }; // 热区组件被谁触发
}

export class HotZoneEditor extends Component<IProps> {
    state = {
        // 热区坐标
        coordinates: [],
        // 图片宽高
        wh: { width: 0, height: 0 },
        // 当前选中的热区下标
        activeIndex: '',
    };

    // 热区容器
    hotZoneRef = createRef<any>();

    componentDidMount(): void {
        // 监听热区创建
        mitt.on('hot-zone-create', this.onCreate);
        // 监听热区改变
        mitt.on('hot-zone-item-change', this.onChangeItem);
        // 监听热区删除
        mitt.on('hot-zone-item-remove', this.onRemoveItem);
    }

    componentDidUpdate(prevProps: IProps) {
        const { visible, hotZoneList, imgUrl, onChange } = this.props;
        if (visible !== prevProps.visible) {
            if (visible) {
                this.setState({ coordinates: hotZoneList, wh: getUrlImgSize(imgUrl) });
            }
        }

        // 当背景图片改变时
        if (imgUrl !== prevProps.imgUrl) {
            if (imgUrl) {
                this.setState({ wh: getUrlImgSize(imgUrl) }, () => {
                    const { wh } = this.state;
                    setTimeout(() => {
                        onChange({
                            imgWh: getImgSize(wh.width, wh.height),
                        });
                    }, 100);
                });
            }
        }
    }

    componentWillUnmount(): void {
        mitt.off('hot-zone-create', this.onCreate);
        mitt.off('hot-zone-item-change', this.onChangeItem);
        mitt.off('hot-zone-item-remove', this.onRemoveItem);
    }

    // 包装热区选项
    WrapHotZoneItem = (props: any) => {
        const selector: any = customSelectorConfig;
        const { activeIndex } = this.state;
        return (
            <HotZoneItem
                key={activeIndex}
                activeIndex={activeIndex}
                {...props}
                selector={selector}
            />
        );
    };

    // 改变热区大小及位置
    onChangeZone = (args: any[]) => {
        const { form, wrapName, onChange, isFormTrigger } = this.props;
        const formValue = form.getFieldsValue();
        this.setState({ coordinates: args[2] }, () => {
            if (!isFormTrigger) {
                onChange({ ...formValue, [wrapName]: this.state.coordinates });
            } else {
                form.setFieldsValue({ ...formValue, [wrapName]: this.state.coordinates });
            }
        });
    };

    // 热区创建
    onCreate = (val: any) => {
        const { arr, item, onChange } = val;
        const { wh } = this.state;
        const { width, height } = getImgSize(wh.width, wh.height);
        const hotWidth = 100;
        const hotHeight = 100;
        const x = width / 2 - hotWidth / 2;
        const y = height / 2 - hotHeight / 2;
        const cur = { ...item, x, y, width: hotWidth, height: hotHeight, date: Date.now() };

        this.setState({ coordinates: [...this.state.coordinates, cur] }, () =>
            onChange([...arr, cur])
        );
    };

    // 改变热区内容细信息
    onChangeItem = (val: any) => {
        const { item, index, onChange } = val;
        const { coordinates } = this.state;
        const newItems = [...coordinates];
        newItems.splice(index, 1, item);

        this.setState({ coordinates: newItems }, () => {
            this.onChangeColor(index);
            onChange(newItems);
        });
    };

    // 删除热区
    onRemoveItem = (val: any) => {
        const { index, onChange } = val;
        const newData = this.state.coordinates.filter((_, i) => i !== index);
        this.setState({ coordinates: newData }, () => onChange(newData));
    };

    // 确定
    onOk = () => {
        const { form, result, index, onChange, onClose } = this.props;
        const { coordinates, wh } = this.state;
        const formValue = form.getFieldsValue();

        onChange({
            ...formValue,
            [result]: coordinates,
            imgWh: getImgSize(wh.width, wh.height),
        });
        onClose(index);
    };

    // 改变热区颜色
    onChangeColor = (index: any) => {
        this.setState({ activeIndex: String(index) }, () => {
            const hotZoneWrap = this.hotZoneRef?.current?.container;
            if (!!hotZoneWrap) {
                const children = [...hotZoneWrap.children];
                children.forEach((el, i) => {
                    if (el.tagName === 'DIV') {
                        if (this.state.activeIndex === String(i - 1)) {
                            el.style.borderColor = '#409EFF';
                            el.style.background = '#E6F7FF';
                        } else {
                            el.style.borderColor = 'rgb(255, 63, 63)';
                            el.style.background = 'rgb(255, 200, 200)';
                        }
                    }
                });
            }
        });
    };

    onDraw = (args: any) => {};

    // 改变热区坐标
    changeCoordinate = (args: any) => this.onChangeColor(args[1]);

    // 双击热区
    onDoubleClick = (args: any) => this.onChangeColor(args[1]);

    // 拖拽热区
    onDragStart = (result: any) => this.onChangeColor(result.source.index);

    // 拖拽结束
    onDragEnd = (result: any) => this.onChangeColor(result.destination.index);

    // 渲染tab标签区域
    renderTabLabelArea = (bgWidth: number) => {
        const { imgUrl, tabWidth } = this.props.source.data;
        const iframeClientWidth = 375;
        const ratio = tabWidth / iframeClientWidth;
        const width = bgWidth * ratio;

        return (
            <div
                style={{
                    width: '100%',
                    height: 'auto',
                    background: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    fontSize: '20px',
                    position: 'absolute',
                    top: 16.56,
                    left: 0,
                    zIndex: 999,
                }}
            >
                <img src={imgUrl} style={{ width, height: 'auto', opacity: 0 }} alt="" />
                <span
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    tab标签区域
                </span>
            </div>
        );
    };

    render() {
        const {
            wrapName,
            visible,
            imgUrl,
            linkTypes,
            linkTypeMode,
            maxCount,
            hotZoneList,
            index,
            form: { getFieldDecorator },
            onClose,
            readOnly = false,
            source = { from: '', data: {} },
        } = this.props;
        const { coordinates } = this.state;

        const modalTitle = (
            <div
                className="modal-title"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div className="modal-title-text">热区编辑器</div>
                <div className="modal-title-options">
                    <Button style={{ margin: '0 10px' }} onClick={() => onClose(index)}>
                        取消
                    </Button>
                    {!readOnly ? (
                        <Button type="primary" onClick={this.onOk}>
                            完成
                        </Button>
                    ) : (
                        ''
                    )}
                </div>
            </div>
        );

        const wh = getUrlImgSize(imgUrl);
        const { width, height } = getImgSize(wh.width, wh.height);
        const isFromTab = source.from === 'tab';
        return (
            <Modal
                title={modalTitle}
                width={1200}
                closable={false}
                visible={visible}
                maskClosable={false}
                footer={null}
                destroyOnClose={true}
            >
                <div
                    className="hot-zone-content"
                    style={{ width: '100%', height: '600px', display: 'flex' }}
                >
                    <div
                        className="hot-zone-content-left"
                        style={{
                            width: '600px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'auto',
                            position: 'relative',
                        }}
                    >
                        <div style={{ width, height, position: 'relative' }}>
                            {isFromTab ? this.renderTabLabelArea(width) : null}
                            <HotZoneWrap
                                className="hot-zone-wrap"
                                ref={this.hotZoneRef}
                                src={imgUrl}
                                width={width}
                                height={height}
                                coordinates={coordinates}
                                onDraw={(...args: any) => this.onDraw(args)}
                                onDrag={(...args: any[]) => this.onChangeZone(args)}
                                onResize={(...args: any[]) => this.onChangeZone(args)}
                                onChange={(...args: any) => this.changeCoordinate(args)}
                                onDelete={(...args: any[]) => this.onChangeZone(args)}
                                onDoubleClick={(...args: any) => this.onDoubleClick(args)}
                                hidden={false}
                            />
                        </div>
                    </div>
                    <div
                        className="hot-zone-content-right"
                        style={{
                            flex: '1',
                            overflowY: 'auto',
                            padding: '0 10px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <div
                            style={{
                                marginBottom: '10px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    width: '10px',
                                    height: '14px',
                                    background: '#409EFF',
                                    marginRight: '10px',
                                }}
                            />
                            <div>热区管理</div>
                        </div>

                        <Form>
                            <FormItem>
                                {getFieldDecorator(wrapName, {
                                    initialValue: hotZoneList,
                                })(
                                    <EntryList
                                        linkTypes={linkTypes}
                                        linkTypeMode={linkTypeMode}
                                        maxCount={maxCount}
                                        renderItem={this.WrapHotZoneItem}
                                        renderTips={HotZoneTips}
                                        onDragStart={(e) => this.onDragStart(e)}
                                        onDragEnd={(e) => this.onDragEnd(e)}
                                    />
                                )}
                            </FormItem>
                        </Form>
                    </div>
                </div>
            </Modal>
        );
    }
}
